import { z } from 'zod';
/**
 * KYC status state machine.
 *
 * Phase 14 drives it for real: the Sumsub webhook (`applicantReviewed`) maps
 * to these statuses through {@link kycStatusFromSumsub}, and in the default
 * `KYC_PROVIDER=manual` mode an admin document approval moves it instead. See
 * `docs/adr/ADR-kyc-verification.md`.
 *
 * Ladder: `none → pending → approved | rejected | resubmission`. A
 * `resubmission` (provider asked for new docs) loops back to `pending`. A
 * `rejected` deal is terminal unless an admin reopens it to `pending`.
 */
export const KycStatusSchema = z.enum([
    'none', // no KYC started (default for every user)
    'pending', // documents submitted, provider reviewing
    'approved', // provider approved (unlocks high-value bids)
    'rejected', // provider rejected (terminal unless reopened)
    'resubmission', // provider requested new/corrected documents
]);
export const KYC_STATUSES = KycStatusSchema.options;
/** The default KYC status of a user who has never started verification. */
export const DEFAULT_KYC_STATUS = 'none';
/**
 * Allowed KYC transitions. Key = current status, value = statuses it may move
 * to. `resubmission` loops back to `pending`; `rejected` may be reopened to
 * `pending` (admin/appeal); `approved` is effectively terminal.
 */
export const KYC_TRANSITIONS = {
    none: ['pending'],
    pending: ['approved', 'rejected', 'resubmission'],
    resubmission: ['pending'],
    rejected: ['pending'],
    approved: [],
};
/**
 * Pure KYC transition guard. A no-op (`from === to`) is rejected; otherwise
 * `to` must be listed in {@link KYC_TRANSITIONS} for `from`.
 */
export function canKycTransition(from, to) {
    const fromAllowed = KYC_TRANSITIONS[from];
    const toExists = KYC_TRANSITIONS[to];
    if (fromAllowed === undefined || toExists === undefined) {
        return { ok: false, code: 'UNKNOWN_STATUS' };
    }
    if (from === to) {
        return { ok: false, code: 'SAME_STATUS' };
    }
    if (!fromAllowed.includes(to)) {
        return { ok: false, code: 'INVALID_KYC_TRANSITION' };
    }
    return { ok: true };
}
/**
 * Normalize an unknown/absent stored value to a KycStatus. A user document
 * written before Phase 13 has no `kycStatus` — its effective status is `none`.
 */
export function effectiveKycStatus(value) {
    const parsed = KycStatusSchema.safeParse(value);
    return parsed.success ? parsed.data : DEFAULT_KYC_STATUS;
}
/**
 * `kyc.status_changed` outbox event — appended in the SAME transaction as the
 * user `kycStatus` flip.
 */
export const KycStatusChangedEventSchema = z.object({
    type: z.literal('kyc.status_changed'),
    data: z.object({
        userId: z.string(),
        from: KycStatusSchema,
        to: KycStatusSchema,
        /** Provider reference — the Sumsub applicantId, or the reviewed documentId. */
        providerRef: z.string().nullish(),
        when: z.string(), // ISO timestamp
    }),
});
// ---------------------------------------------------------------------------
// Phase 14 — the verification LADDER, the identity provider, and the Sumsub
// webhook contract. Everything below is additive to the Phase 13 state machine
// above: `kycStatus` stays the provider-facing state, the ladder is what the
// business gates read.
// ---------------------------------------------------------------------------
/**
 * Derived verification level (Phase 14). NEVER stored — it is recomputed from
 * the user's own timestamps on every read, so there is no backfill and no
 * field that can drift out of sync with the facts it summarises.
 *
 *   0 — nothing
 *   1 — `emailVerified` is a Date            (phase 9)
 *   2 — `phoneVerifiedAt` is a Date          (phase 14, OTP)
 *   3 — `identityVerifiedAt` is a Date       (Sumsub GREEN, or an admin approve)
 *   4 — a deposit (`biddingPower > 0`) or `exposureExempt` (phase 10)
 *
 * The ladder is CUMULATIVE: the level is the highest rung reachable without
 * skipping one. A user who passed Sumsub but never confirmed a phone is
 * level 1, not 3 — otherwise "level >= 2" would stop meaning "we can reach
 * this person", which is the only thing the low-value bid gate is for.
 *
 * NOTE (name collision, deliberate): `users.verificationLevel` in Mongo is a
 * LEGACY STRING ('basic' | 'standard' | 'premium') written by
 * KycWorkflowService since the web era. This numeric ladder is a different
 * concept with a different home — it is never written to that field.
 */
export const VERIFICATION_LEVELS = [0, 1, 2, 3, 4];
export const VerificationLevelSchema = z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
]);
/** What a user must still do to climb one rung. */
export const VerificationRequirementSchema = z.enum(['email', 'phone', 'identity', 'deposit']);
/** Rung index -> the requirement that unlocks it. Index 0 has no requirement. */
export const VERIFICATION_LADDER = {
    1: 'email',
    2: 'phone',
    3: 'identity',
    4: 'deposit',
};
/**
 * Who asserted the identity. `manual` = an admin approved an uploaded
 * document; `sumsub` = the provider returned GREEN; `diia` is RESERVED and
 * not implemented (see docs/adr/ADR-kyc-verification.md — pending an answer
 * from integration.diia.gov.ua about access for a private company).
 */
export const IdentityProviderSchema = z.enum(['sumsub', 'diia', 'manual']);
/**
 * A stored value counts as "done" only when it really is a Date (or an ISO
 * string that parses). Exported because the API reports the individual rungs
 * (`emailVerified`, `phoneVerified`, ...) alongside the cumulative level, and
 * both readings must agree on what "proven" means.
 */
export function isVerificationTimestamp(value) {
    if (value instanceof Date)
        return !Number.isNaN(value.getTime());
    if (typeof value === 'string' && value.length > 0) {
        return !Number.isNaN(new Date(value).getTime());
    }
    return false;
}
/** Pure ladder derivation — no I/O, same function on the server and in tests. */
export function deriveVerificationLevel(facts) {
    if (!isVerificationTimestamp(facts.emailVerified))
        return 0;
    if (!isVerificationTimestamp(facts.phoneVerifiedAt))
        return 1;
    if (!isVerificationTimestamp(facts.identityVerifiedAt))
        return 2;
    const deposit = typeof facts.biddingPower === 'number' ? facts.biddingPower : 0;
    if (!(deposit > 0) && facts.exposureExempt !== true)
        return 3;
    return 4;
}
/** The single next rung, or null at the top of the ladder. */
export function nextVerificationStep(level) {
    if (level >= 4)
        return null;
    const next = (level + 1);
    return { level: next, requirement: VERIFICATION_LADDER[next] };
}
// ---------------------------------------------------------------------------
// «Запросити документ» (KYC defects, 2026-09-30). An admin asks a client to
// upload an identity document: `POST /api/admin/users/:userId/kyc/document-request`
// stores `users.kycDocumentRequest` and appends `kyc.document_requested`.
// ---------------------------------------------------------------------------
/** The longest note an admin may attach to a document request. */
export const KYC_DOCUMENT_REQUEST_NOTE_MAX = 500;
/** `POST /api/admin/users/:userId/kyc/document-request` body. */
export const KycDocumentRequestInputSchema = z.object({
    note: z.string().trim().max(KYC_DOCUMENT_REQUEST_NOTE_MAX).nullish(),
});
/**
 * `users.kycDocumentRequest` on the wire (ISO timestamp). A re-request
 * OVERWRITES it — there is only ever the latest request.
 */
export const KycDocumentRequestSchema = z.object({
    requestedAt: z.string(),
    /** Admin userId who asked. Admin-facing only — never sent to the client. */
    requestedBy: z.string(),
    note: z.string().nullable(),
});
/** The client-facing view of an OPEN request — who asked is internal. */
export const KycDocumentRequestViewSchema = KycDocumentRequestSchema.omit({
    requestedBy: true,
});
function timestampOf(value) {
    if (value instanceof Date)
        return Number.isNaN(value.getTime()) ? null : value.getTime();
    if (typeof value === 'string' && value.length > 0) {
        const time = new Date(value).getTime();
        return Number.isNaN(time) ? null : time;
    }
    return null;
}
/**
 * Whether a stored document request still asks the client for something.
 *
 * DERIVED, never cleared by a write: the request stays on the user as a
 * record of what was asked, and it stops counting as soon as
 *   - the identity is verified (rung 3 — there is nothing left to ask), or
 *   - a document was uploaded AFTER the request (the client answered it; the
 *     review of that document is the admin's move now).
 * Deriving it keeps the upload path and the KYC state machine free of a
 * second write that could drift, and a re-request after an upload re-opens it
 * naturally because its `requestedAt` is newer.
 */
export function isKycDocumentRequestOpen(request, facts) {
    if (!request)
        return false;
    const requestedAt = timestampOf(request.requestedAt);
    if (requestedAt === null)
        return false;
    if (facts.identityVerified)
        return false;
    const uploadedAt = timestampOf(facts.lastDocumentUploadedAt);
    return uploadedAt === null || uploadedAt < requestedAt;
}
/** `GET /api/users/me/verification` body. */
export const VerificationStateSchema = z.object({
    level: VerificationLevelSchema,
    emailVerified: z.boolean(),
    phoneVerified: z.boolean(),
    identityVerified: z.boolean(),
    identityProvider: IdentityProviderSchema.nullable(),
    depositFunded: z.boolean(),
    kycStatus: KycStatusSchema,
    next: z
        .object({
        level: VerificationLevelSchema,
        requirement: VerificationRequirementSchema,
    })
        .nullable(),
    /**
     * An OPEN admin document request (see {@link isKycDocumentRequestOpen}),
     * null when there is none. Optional so older bodies still parse.
     */
    documentRequest: KycDocumentRequestViewSchema.nullable().optional(),
});
/**
 * The 403 body every verification gate answers with. `next` is what the
 * client renders in the ladder modal, so the refusal itself tells the user
 * the one thing to do.
 */
export const VERIFICATION_LEVEL_REQUIRED = 'VERIFICATION_LEVEL_REQUIRED';
/**
 * Default bid amount (CHF) at or above which identity verification (level 3)
 * is required; below it, level 2 (a reachable phone) is enough. OWNER
 * DECISION 2026-09-21: 8 000 CHF — chosen to sit at or below the 400 000 UAH
 * threshold operation of Ukraine's law 361-IX rather than above it, so a bid
 * that may attract threshold requirements cannot pass on a phone alone. It
 * supersedes the 15 000 recorded on 2026-09-20. See
 * docs/adr/ADR-kyc-verification.md §3. Overridable per environment with
 * `KYC_BID_THRESHOLD`; this constant is the ONLY default — `validation.ts`
 * imports it rather than repeating the number.
 */
export const DEFAULT_KYC_BID_THRESHOLD_CHF = 8_000;
/** Minimum level to place any bid below the threshold. */
export const BID_MIN_VERIFICATION_LEVEL = 2;
/** Minimum level for a bid at/above the threshold, and for a relay confirm. */
export const HIGH_VALUE_VERIFICATION_LEVEL = 3;
// ----------------------------------------------------------------- Sumsub
/** Sumsub applicant, as this codebase needs it (the API returns much more). */
export const KycApplicantSchema = z
    .object({
    id: z.string(),
    externalUserId: z.string(),
    levelName: z.string().nullish(),
    createdAt: z.string().nullish(),
    review: z
        .object({
        reviewStatus: z.string().nullish(),
        reviewResult: z
            .object({
            reviewAnswer: z.string().nullish(),
            reviewRejectType: z.string().nullish(),
        })
            .nullish(),
    })
        .nullish(),
})
    .passthrough();
/** Sumsub WebSDK access token response. */
export const KycAccessTokenSchema = z
    .object({ token: z.string(), userId: z.string() })
    .passthrough();
/**
 * Sumsub webhook payload (the fields this integration reads). Sumsub sends a
 * superset and adds fields over time — `.passthrough()` so a new one never
 * turns a legitimate decision into a 400.
 */
export const KycDecisionSchema = z
    .object({
    applicantId: z.string(),
    /** Sumsub echoes our `users._id` here (we set it on applicant creation). */
    externalUserId: z.string(),
    /** `applicantReviewed`, `applicantPending`, `applicantCreated`, ... */
    type: z.string(),
    /**
     * When SUMSUB produced this event — `"2020-06-24 05:29:12"` (UTC, no zone
     * suffix). Read by the replay guard, never trusted for anything else.
     */
    createdAt: z.string().nullish(),
    /** Same instant with milliseconds — `"2020-06-24 05:29:12.328"`. */
    createdAtMs: z.string().nullish(),
    reviewStatus: z.string().nullish(),
    reviewResult: z
        .object({
        /** 'GREEN' | 'RED' */
        reviewAnswer: z.string().nullish(),
        /** 'FINAL' (terminal) | 'RETRY' (client may resubmit) */
        reviewRejectType: z.string().nullish(),
        moderationComment: z.string().nullish(),
        clientComment: z.string().nullish(),
        rejectLabels: z.array(z.string()).nullish(),
    })
        .nullish(),
})
    .passthrough();
/**
 * Sumsub decision -> internal KYC status. `null` = the event carries no
 * decision (applicantCreated, or a reviewed event with no reviewResult) and
 * must NOT move the state machine.
 *
 *   applicantReviewed + GREEN         -> approved
 *   applicantReviewed + RED + RETRY   -> resubmission
 *   applicantReviewed + RED + FINAL   -> rejected
 *   applicantPending / applicantOnHold-> pending
 */
export function kycStatusFromSumsub(decision) {
    switch (decision.type) {
        case 'applicantReviewed': {
            const answer = decision.reviewResult?.reviewAnswer;
            if (answer === 'GREEN')
                return 'approved';
            if (answer !== 'RED')
                return null;
            return decision.reviewResult?.reviewRejectType === 'RETRY' ? 'resubmission' : 'rejected';
        }
        case 'applicantPending':
        case 'applicantOnHold':
            return 'pending';
        default:
            return null;
    }
}
/**
 * Parse a Sumsub event timestamp into a Date, or null when it is unusable.
 *
 * REPLAY DEFENCE (review finding F6): a captured, validly signed `GREEN`
 * delivery can be replayed after a later `RED`, and because
 * `rejected → pending → approved` are both legal transitions it would
 * re-approve the applicant and restore rung 3. The only thing that
 * distinguishes the replay from a fresh decision is WHEN the provider says it
 * was made, so the webhook compares this against the stored `kycUpdatedAt`.
 *
 * Sumsub sends `"YYYY-MM-DD HH:mm:ss(.SSS)"` with NO zone suffix, documented
 * as UTC — parsing that with bare `new Date()` is implementation-defined and
 * would silently shift by the server's offset, so the `Z` is added here.
 * Epoch numbers and full ISO strings are accepted too, defensively.
 */
export function parseSumsubTimestamp(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? new Date(value) : null;
    }
    if (typeof value !== 'string' || value.length === 0)
        return null;
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
        const epoch = Number(trimmed);
        return Number.isFinite(epoch) ? new Date(epoch) : null;
    }
    const spaceSeparated = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/.exec(trimmed);
    const normalized = spaceSeparated ? `${spaceSeparated[1]}T${spaceSeparated[2]}Z` : trimmed;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}
/**
 * Structured fields a KYC document upload carries (Phase 14). These REPLACE
 * the OCR guess the web-era pipeline made from an empty text layer: the
 * client types what the document says, an admin verifies it against the file.
 */
export const KycDocumentUploadSchema = z.object({
    documentType: z.string().min(1).max(64),
    documentNumber: z.string().max(64).optional(),
    firstName: z.string().max(128).optional(),
    lastName: z.string().max(128).optional(),
    birthDate: z.string().max(32).optional(),
    issueDate: z.string().max(32).optional(),
    expiryDate: z.string().max(32).optional(),
    issuedBy: z.string().max(256).optional(),
    citizenship: z.string().max(128).optional(),
    taxID: z.string().max(64).optional(),
    /** KYB uploads bind to the dealer application they support. */
    dealerApplicationId: z.string().max(64).optional(),
});
/**
 * `documentType` of a file an admin attaches to a client's record — a contract,
 * a signed agreement, a payment slip. Stored next to the KYC documents but NOT
 * a KYC event: it never puts the client back «in review» and never enters the
 * verification queue.
 */
export const DOCUMENT_ATTACHMENT_TYPE = 'other';
/** Hard ceiling for a KYC upload (bytes). Anything larger is a 413. */
export const MAX_KYC_FILE_BYTES = 10 * 1024 * 1024;
/** S3 prefix for KYC objects — a private, server-side-encrypted namespace. */
export const KYC_S3_PREFIX = 'kyc';
/** Days a soft-deleted document survives before the retention cron erases it. */
export const KYC_RETENTION_DAYS = 30;
/** Presigned GET lifetime for a KYC document (seconds). */
export const KYC_PRESIGNED_URL_TTL_SECONDS = 300;
//# sourceMappingURL=kyc.js.map